import JsBarcode from "jsbarcode";
import logo from "../assets/ops-banner.png";

// Three copies on one A4 sheet, each printed in a 1/3-page band.
const COPIES = ["Consignor Copy", "POD Copy", "Accounts Copy"];

const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const val = (v, fallback = "-") =>
  v === undefined || v === null || v === "" ? fallback : esc(v);

const check = (checked, label) =>
  `<span class="chk"><i class="box${checked ? " on" : ""}"></i>${label}</span>`;

const barcodeSvg = (value) => {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  JsBarcode(svg, value || "N/A", {
    format: "CODE128",
    width: 1.3,
    height: 26,
    displayValue: true,
    fontSize: 9,
    margin: 0,
  });
  return svg.outerHTML;
};

const buildCopy = (d, copyLabel, barcode, logoUrl) => {
  const pay = d?.paymentType || {};
  const payMode = d?.paymentMode || {};
  const docs = d?.documentsReceived || {};
  const vas = d?.valueAddedService || {};
  const mode = d?.modeOfPayment || {};
  const c = d?.charges || {};

  const party = (title, p) => `
    <div class="cell party">
      <div class="th">${title}</div>
      <div class="pad">
        <div><b>Name:</b> ${val(p?.name, "")}</div>
        <div><b>Address:</b> ${val(p?.address, "")}</div>
        <div><b>Phone:</b> ${val(p?.phoneNo, "")}${
          p?.gst ? ` &nbsp; <b>GST:</b> ${esc(p.gst)}` : ""
        }</div>
      </div>
    </div>`;

  const charges = [
    ["Base Freight", c.baseFreight],
    ["Processing", c.processingCharge],
    ["Fuel", c.fuelSurcharge],
    ["ROV", c.rovCharge],
    ["FOD", c.fodCharge],
    ["Handling", c.handlingCharge],
    ["COD/DOD", c.codDodCharge],
    ["Special Delivery", c.specialDelivery],
    ["Other", c.otherCharges],
    ["Sub Total", c.subTotal],
    ["GST (18%)", c.gst],
    ["Grand Total", c.grandTotal],
  ]
    .map(
      ([l, v], i) =>
        `<div class="chg${i >= 9 ? " strong" : ""}"><span>${l}</span><span>${val(
          v,
          "0",
        )}</span></div>`,
    )
    .join("");

  return `
  <section class="copy">
    <div class="head">
      <img src="${logoUrl}" alt="OPS" />
      <div class="badge">${copyLabel}</div>
      <div class="bc">${barcode}</div>
    </div>

    <div class="row4 box-b">
      <div class="cell"><b>Origin:</b> ${val(d?.origin, "N/A")}</div>
      <div class="cell"><b>Destination:</b> ${val(d?.destination, "N/A")}</div>
      <div class="cell"><b>Mode:</b> ${val(d?.transportMode, "N/A")}</div>
      <div class="cell"><b>Date / Time:</b> ${val(d?.bookingDate, "")} | ${val(
        d?.bookingTime,
        "",
      )}</div>
    </div>

    <div class="row2 box-b">${party("FROM", d?.from)}${party("TO", d?.to)}</div>

    <table class="pkg box-b">
      <thead><tr>
        <th>Invoice No</th><th>Invoice Date</th><th>Invoice Value</th>
        <th>E-Way Bill No</th><th>Boxes</th><th>Dimension</th><th>Type</th>
      </tr></thead>
      <tbody><tr>
        <td>${val(d?.invoiceNo)}</td><td>${val(d?.invoiceDate)}</td>
        <td>${val(d?.invoiceValue)}</td><td>${val(d?.eWayBillNo)}</td>
        <td>${val(d?.numberOfBoxes)}</td><td>${val(d?.boxDimension)}</td>
        <td>${val(d?.packageType)}</td>
      </tr></tbody>
    </table>

    <div class="main">
      <div class="left">
        <div class="grid4">
          <div><b>Said to Contain:</b> ${val(d?.saidToContain, "N/A")}</div>
          <div><b>Risk Coverage:</b> ${val(d?.riskCoverage, "N/A")}</div>
          <div><b>Policy &amp; Validity:</b> ${val(d?.policyAndValidity, "N/A")}</div>
          <div><b>No. of Documents:</b> ${val(d?.numberOfDocuments, "N/A")}</div>
        </div>
        <div class="line"><b>Payment Mode:</b>
          ${check(payMode.cash, "CASH")}${check(payMode.dd, "DD")}${check(
            payMode.cheque,
            "CHEQUE",
          )}
          &nbsp; COD: ${val(d?.codAmount, "0")} &nbsp; Words: ${val(d?.amountInWords)}
        </div>
        <div class="line"><b>Documents Received:</b>
          ${check(docs.invoice, "Invoice")}${check(docs.eWaybill, "E-Way")}${check(
            docs.others,
            "Others",
          )}
        </div>
        <div class="line"><b>Value Added Services:</b>
          ${check(vas.appointmentDelivery, "Appointment")}${check(
            vas.specialDelivery,
            "Special",
          )}
        </div>
        <div class="decl">
          I/We have carefully checked and verified the contents of this consignment
          note and hereby agree to the terms and conditions set out on the reverse
          of the consignor copy and declare the contents on the waybill are true and
          correct. The To-pay (POD) Freight amount has my/our consent and will be
          paid by the consignee or any taxes at the time of delivery.
        </div>
        <div class="sigrow">
          <div class="sig" style="flex:1.25"><b>Consignor's Signature</b></div>
          <div class="sig"><b>Consignee Stamp</b></div>
          <div class="sig" style="flex:1.25"><b>Name &amp; Sign</b></div>
          <div class="sig" style="flex:.7"><b>Date</b></div>
        </div>
      </div>

      <div class="right">
        <div class="wt">
          <div><span>Actual Wt</span><b>${val(d?.actualWeight, "0")}</b></div>
          <div><span>Charged Wt</span><b>${val(d?.chargedWeight, "0")}</b></div>
        </div>
        <div class="line"><b>Payment Type:</b>
          ${check(pay.credit, "CREDIT")}${check(pay.paid, "PAID")}${check(
            pay.fod,
            "FOD",
          )}
        </div>
        <div class="chgs">${charges}</div>
        <div class="line"><b>Mode of Payment:</b>
          ${check(mode.cash, "Cash")}${check(mode.chequeDd, "Cheque/DD")}${check(
            mode.neftRtgs,
            "NEFT/RTGS",
          )}
        </div>
        <div>Cheque / NEFT No: ${val(d?.chequeNeftNo)} &nbsp; Bank: ${val(
          d?.bank,
        )}</div>
        <div>Words: ${val(d?.amountInWordsPayment)}</div>
      </div>
    </div>
  </section>`;
};

const STYLES = `
  @page { size: A4 portrait; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 9px; line-height: 1.3; color: #000;
         -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .sheet { width: 210mm; height: 297mm; padding: 4mm 6mm; display: flex; flex-direction: column; }
  /* each copy gets exactly one third of the sheet */
  .copy { flex: 1 1 0; min-height: 0; overflow: hidden; padding: 1.5mm 0;
          border-bottom: 1px dashed #777; display: flex; flex-direction: column; }
  .copy:last-child { border-bottom: 0; }
  b { font-weight: 700; }
  .head { display: flex; align-items: center; justify-content: space-between; gap: 6px;
          border-bottom: 1px solid #000; padding-bottom: 2px; }
  .head img { height: 38px; object-fit: contain; }
  .badge { padding: 1px 8px; font-weight: 700; font-size: 15px;
           letter-spacing: .4px; text-transform: uppercase; }
  .bc svg { height: 36px; width: auto; display: block; }
  .box-b { border: 1px solid #000; border-top: 0; }
  .row4 { display: grid; grid-template-columns: repeat(4, 1fr); }
  .row4 .cell { padding: 1.5px 4px; border-right: 1px solid #000; }
  .row4 .cell:last-child { border-right: 0; }
  .row2 { display: grid; grid-template-columns: 1fr 1fr; }
  .party { border-right: 1px solid #000; }
  .row2 .party:last-child { border-right: 0; }
  .th { text-align: center; font-weight: 700; border-bottom: 1px solid #000; padding: 1px 0; }
  .pad { padding: 2px 4px; }
  table.pkg { width: 100%; border-collapse: collapse; }
  table.pkg th, table.pkg td { border-right: 1px solid #000; padding: 1.5px 3px; text-align: center; }
  table.pkg th { border-bottom: 1px solid #000; }
  table.pkg th:last-child, table.pkg td:last-child { border-right: 0; }
  .main { flex: 1 1 auto; min-height: 0; display: grid; grid-template-columns: 60% 40%;
          border: 1px solid #000; border-top: 0; }
  .left { padding: 2px 4px; border-right: 1px solid #000; display: flex; flex-direction: column; gap: 2px; }
  .right { padding: 2px 4px; display: flex; flex-direction: column; gap: 2px; }
  .grid4 { display: grid; grid-template-columns: 1fr 1fr; gap: 1px 6px; padding-bottom: 2px; border-bottom: 1px solid #000; }
  .line { padding-top: 1px; }
  .chk { margin-left: 5px; white-space: nowrap; }
  .box { display: inline-block; width: 6px; height: 6px; border: 1px solid #000; margin-right: 2px; vertical-align: -1px; }
  .box.on { background: #000; }
  .decl { font-size: 7.5px; font-style: italic; line-height: 1.2; }
  /* stamp / signature boxes fill all remaining space in the left column */
  .sigrow { flex: 1 1 auto; display: flex; gap: 3px; min-height: 40px; margin-top: 3px; }
  .sig { flex: 1; padding: 1.5px 3px; font-size: 7px; line-height: 1.15; }
  .wt { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid #000; }
  .wt div { display: flex; justify-content: space-between; padding: 1.5px 4px; }
  .wt div:first-child { border-right: 1px solid #000; }
  .chgs { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid #000; border-right: 0; border-bottom: 0; }
  .chg { display: flex; justify-content: space-between; padding: 1.5px 4px;
         border-right: 1px solid #000; border-bottom: 1px solid #000; }
  .chg.strong { font-weight: 700; background: #eee; }
`;

export const printDocket = (formData) => {
  if (!formData) return;

  const logoUrl = new URL(logo, window.location.origin).href;
  const barcode = barcodeSvg(formData?.docketNumber);
  const copies = COPIES.map((label) =>
    buildCopy(formData, label, barcode, logoUrl),
  ).join("");

  const printWindow = window.open("", "_blank");
  if (!printWindow) return;

  printWindow.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Docket ${esc(formData?.docketNumber)}</title>
    <style>${STYLES}</style>
  </head>
  <body><div class="sheet">${copies}</div></body>
</html>`);
  printWindow.document.close();

  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };
};
